// MOTOR IA DE AJUSTES BÁSICOS (Fase 2) — decisor ÚNICO de los 13 parámetros básicos.
// Responsabilidad EXCLUSIVA: Temperature, Tint, Exposure, Contrast, Highlights, Shadows,
// Whites, Blacks, Texture, Clarity, Dehaze, Vibrance, Saturation. NADA MÁS.
// El preset/estilo controla el resto del look y NUNCA estos 13 (aislamiento en el backend).
//
// Principios (regla del usuario):
//  - Analiza CADA foto individualmente; valores distintos por foto (no un preset genérico).
//  - "No tocar" es válido: si la foto no lo necesita, el parámetro queda en 0 (o WB = As Shot).
//  - Respeta la intención de luz (low/high-key, contraluz, flash). No crea HDR ni look.
import { computeBasic } from "./exposureEngine";
import { computeWhiteBalance } from "./whiteBalance";
import { computeMeter } from "./hybridMetering";

export const AI_BASIC_KEYS = [
  "temperature", "tint", "exposure", "contrast", "highlights", "shadows",
  "whites", "blacks", "texture", "clarity", "dehaze", "vibrance", "saturation",
];

// Todos a 0 (temp/tint = null → As Shot): la corrección "no tocar".
export const NEUTRAL_AI_BASICS = {
  temperature: null, tint: null, exposure: 0, contrast: 0, highlights: 0, shadows: 0,
  whites: 0, blacks: 0, texture: 0, clarity: 0, dehaze: 0, vibrance: 0, saturation: 0,
};

const DEADZONE = {
  prudent: { exp: 0.06, tone: 4 },
  active: { exp: 0.03, tone: 2 },
};

function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }

// A2: agrega la PIEL de las caras (Fase 1) como referencia prioritaria de WB.
// Pondera por tamaño de cara y nº de muestras; ignora caras sin piel fiable.
function aggregateFaceSkin(faces) {
  if (!faces || !faces.length) return null;
  let r = 0, g = 0, b = 0, wsum = 0;
  for (const f of faces) {
    const sk = f && f.skin;
    if (!sk || !sk.count || !(sk.r > 0)) continue;
    const w = (f.area || 0.01) * Math.min(1, sk.count / 40);
    r += sk.r * w; g += sk.g * w; b += sk.b * w; wsum += w;
  }
  if (!wsum) return null;
  return { r: r / wsum, g: g / wsum, b: b / wsum };
}

function buildReasoning({ exposureClassification, exposure, highlights, shadows, wb, dehaze, vibrance, skinDetected }) {
  const parts = [];
  parts.push(`Fotografía ${exposureClassification}.`);
  if (exposure > 0) parts.push(`Se sube la exposición ${exposure.toFixed(2)} EV.`);
  else if (exposure < 0) parts.push(`Se baja la exposición ${exposure.toFixed(2)} EV.`);
  else parts.push("Exposición conservada.");
  if (highlights < 0) parts.push("Altas luces recuperadas sin aplanarlas.");
  else parts.push("Altas luces conservadas.");
  if (shadows > 0) parts.push("Sombras abiertas de forma controlada.");
  else parts.push("Sombras respetadas (no se levanta el fondo).");
  if (wb.temperature != null) {
    parts.push(`Balance de blancos corregido${skinDetected ? " con la piel como referencia" : ""}.`);
  } else parts.push("Balance de blancos coherente: se deja As Shot.");
  if (dehaze > 0) parts.push("Ligera eliminación de neblina.");
  if (vibrance > 0) parts.push("Pequeña corrección de vibración global.");
  if (exposure === 0 && highlights === 0 && shadows === 0 && wb.temperature == null && dehaze === 0 && vibrance === 0) {
    parts.push("La foto ya está equilibrada: apenas se modifica.");
  }
  return parts.join(" ");
}

// analysis: { photometrics, color, subjectDetected? }
// opts: { mode='balanced', wbStrength=0.5, deadzone='prudent', subjectDetected }
export function computeAiBasics(analysis, opts = {}) {
  const { mode = "balanced", wbStrength = 0.5, deadzone = "prudent" } = opts;
  const pm = analysis && analysis.photometrics;
  const color = analysis && analysis.color;
  const subjectDetected = opts.subjectDetected ?? analysis?.subjectDetected ?? false;
  const dz = DEADZONE[deadzone] || DEADZONE.prudent;
  const snap = (v, t) => (Math.abs(v) < t ? 0 : Math.round(v));

  // --- Metering HÍBRIDO (A1): incorpora las caras de Fase 1 como señal prioritaria acotada ---
  const faces = (analysis && analysis.faces) || null;
  const met = pm ? computeMeter(pm, faces) : { meter: null, faceWeight: 0, subjectLuma: null, reason: "sin fotometría" };
  const meterOverride = (faces && faces.length) ? met.meter : null;

  // --- Exposición y tono (6): motor v2 + zona muerta "no tocar" ---
  // Subject-target: expone PARA la persona solo si la cara pesa de verdad (no una cara diminuta/borrosa).
  const subjectTarget = meterOverride != null && (met.faceWeight || 0) >= 0.25;
  const base = pm ? computeBasic(pm, mode, meterOverride, met.subjectLuma, { subjectTarget })
    : { exposure: 0, contrast: 0, highlights: 0, shadows: 0, whites: 0, blacks: 0 };
  const exposure = Math.abs(base.exposure) < dz.exp ? 0 : Math.round(base.exposure * 100) / 100;
  const contrast = snap(base.contrast, dz.tone);
  const highlights = snap(base.highlights, dz.tone);
  const shadows = clamp(snap(pm ? base.shadows : 0, dz.tone), -100, 100); // Shadows sin baseline oculto (fuente única: exposureEngine)
  const whites = snap(base.whites, dz.tone);
  const blacks = snap(base.blacks, dz.tone);

  // --- Balance de blancos (2) ---
  // Jerarquía A2: piel FACIAL (Fase 1) → piel agregada → gris-mundo → As Shot.
  // Ambiente por color (golden hour/tungsteno) preserva la calidez deseada.
  const faceSkin = aggregateFaceSkin(faces);
  const wb = computeWhiteBalance(color, {
    strength: wbStrength,
    ambience: (pm && pm.ambience) || "neutral",
    faceSkin,
  });

  // --- Presencia (3): 0 salvo que la fotometría lo justifique (nada de HDR) ---
  const contrastIndex = pm ? pm.contrastIndex : 100;
  const p5 = pm ? pm.p5 : 20;
  const hazy = pm && contrastIndex < 90 && p5 > 30; // velo: poco contraste + negros levantados
  const dehaze = hazy ? clamp(Math.round((90 - contrastIndex) * 0.3), 0, 20) : 0;
  const clarity = 0;
  const texture = 0;

  // --- Color (2): SOLO corrección de equilibrio, no look ---
  let vibrance = 0;
  const saturation = 0;
  if (color) {
    const mx = Math.max(color.meanR, color.meanG, color.meanB);
    const mn = Math.min(color.meanR, color.meanG, color.meanB);
    const sat = mx ? (mx - mn) / mx : 0;
    if (sat < 0.06) vibrance = Math.round(8 * (wbStrength / 0.5)); // imagen muy plana → sube un poco
  }

  const aiBasicAdjustments = {
    temperature: wb.temperature, tint: wb.tint, exposure, contrast, highlights, shadows,
    whites, blacks, texture, clarity, dehaze, vibrance, saturation,
  };

  // --- Auditoría ---
  const meter = pm ? (pm.cP50 || pm.p50) : 128;
  const bands = { conservative: [110, 150], balanced: [105, 155], aggressive: [100, 160] }[mode] || [105, 155];
  const exposureClassification = meter < bands[0] ? "subexpuesta" : (meter > bands[1] ? "sobreexpuesta" : "correcta");
  const skinDetected = wb.skinDetected;
  let editingConfidence = 0.85;
  if (pm && pm.highClip > 0.08 && pm.lowClip > 0.08) editingConfidence -= 0.25; // escena difícil (doble recorte)
  if (!skinDetected && wb.temperature != null) editingConfidence -= 0.1;        // WB sin piel = menos fiable
  editingConfidence = Math.round(clamp(editingConfidence, 0.3, 0.95) * 100) / 100;

  return {
    aiBasicAdjustments,
    editingConfidence,
    exposureClassification,
    subjectDetected: !!subjectDetected,
    facesDetected: faces ? faces.length : 0,
    metering: { meter: met.meter, faceWeight: met.faceWeight, subjectLuma: met.subjectLuma, reason: met.reason },
    skinDetected,
    whiteBalanceConfidence: wb.whiteBalanceConfidence,
    wbSource: wb.skinSource,
    ambience: (pm && pm.ambience) || "neutral",
    subjectLuma: met.subjectLuma != null ? Math.round(met.subjectLuma) : null,
    faceWeight: Math.round((met.faceWeight || 0) * 100) / 100,
    meter: met.meter != null ? Math.round(met.meter) : (pm ? Math.round(pm.cP50 || pm.p50) : null),
    reasoningSummary: buildReasoning({ exposureClassification, exposure, highlights, shadows, wb, dehaze, vibrance, skinDetected }),
  };
}
